import React, { ReactElement, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { ReactComponent as LogoSvg } from '@assets/icons/logo.svg';
import LinkProtected from '@pages/ExternalReviewer/LinkProtected';
import './ExternalReviewer.less';
import {
  ShareReviewProvider,
  useShareReview
} from '@components/ShareReviewProvider';
import AccessClose from '@pages/ExternalReviewer/AccessClose';
import SingInExternalReviewerModal from '@components/Modals/SingInExternalReviewerModal';
import MediaViewer from '@pages/MediaViewer';
import ExternalReviewerMediaLayout from '@layouts/ExternalReviewerMediaLayout';
import { WebSocketClientProvider } from '@hooks/useWebSocketClient';
import { useAuth } from '@hooks/useAuth';
import ExpiredLink from '@components/ExpiredLink';
import AssetsList from '@pages/ExternalReviewer/AssetsList/AssetsList';
import SelectFilesModal from '@components/Modals/SelectFilesModal';
import { addNotificationSubscription, addReviewVisit } from '@api/AssetReview';
import { nonNull } from '@helpers/non-null';
import OnboardingProcessProvider from '@context/OnboardingProcessProvider';

interface ExternalReviewerProps {
  element: ReactElement;
}

export function ExternalReviewerPage({ element }: ExternalReviewerProps) {
  return (
    <div className="external_reviewer_container">
      <div className="external_reviewer_top">
        <LogoSvg />
      </div>
      <div className="external_reviewer_content">{element}</div>
      <div className="external_reviewer_bottom">
        New to StreamWork? Create your <a href="/login">own account</a> and
        start collaborating.
      </div>
    </div>
  );
}

interface ExternalReviewerContainerProps {
  loader: ReactElement;
}

function ExternalReviewerContainer({ loader }: ExternalReviewerContainerProps) {
  const shareReview = useShareReview();
  const {
    reviewId,
    isLoading,
    password,
    passwordRequired,
    passwordDoesNotMatch,
    isDisabled,
    setPassword,
    content,
    isExpired,
    commentMentions
  } = shareReview;
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('assetId');
  const { user } = useAuth();

  const notAvailable =
    isLoading ||
    (passwordRequired && passwordDoesNotMatch) ||
    isDisabled ||
    isExpired;

  useEffect(() => {
    if (notAvailable) return;
    if (user)
      addNotificationSubscription({
        reviewId: nonNull(reviewId),
        password
      });
    addReviewVisit({
      reviewId: nonNull(reviewId),
      password
    });
  }, [user, reviewId, password, notAvailable]);

  if (isExpired) return <ExpiredLink />;
  if (passwordRequired && passwordDoesNotMatch) {
    return (
      <ExternalReviewerPage
        element={
          <LinkProtected
            password={password}
            passwordDoesNotMatch={passwordDoesNotMatch}
            setPassword={setPassword}
            loading={isLoading}
          />
        }
      />
    );
  }

  if (isLoading) return loader;
  if (isDisabled) {
    return (
      <ExternalReviewerPage
        element={<AccessClose type="review" reviewReason="review-disabled" />}
      />
    );
  }

  if (!assetId && content?.assets?.length === 1 && content?.assets[0].id) {
    const asset = content.assets[0];
    const versionId = asset.asset?.versions?.[0]?.id;
    if (versionId) {
      return (
        <Navigate
          to={`/external-reviewer?reviewId=${reviewId}&assetId=${asset.id}&versionId=${versionId}`}
        />
      );
    }
  }

  if (!assetId) {
    return <AssetsList />;
  }

  return (
    <>
      <Routes>
        <Route path="*" element={<ExternalReviewerMediaLayout />}>
          <Route
            index
            element={<MediaViewer commentMentions={commentMentions} />}
          />
        </Route>
      </Routes>
      <SingInExternalReviewerModal />
      <SelectFilesModal />
    </>
  );
}

export default function ExternalReviewer(
  props: ExternalReviewerContainerProps
) {
  const { isAuthenticated, isLoading } = useAuth();
  const initialLoader = useRef(isLoading);
  if (!isLoading) initialLoader.current = false;
  if (!isAuthenticated && initialLoader.current) return props.loader;
  return (
    <OnboardingProcessProvider>
      <WebSocketClientProvider>
        <ShareReviewProvider>
          <ExternalReviewerContainer {...props} />
        </ShareReviewProvider>
      </WebSocketClientProvider>
    </OnboardingProcessProvider>
  );
}
